import { lazy, Suspense, useState } from "react";
import type { Position } from "@/api/types";
import { cancelPendingOrders, closePosition } from "@/api/client";
import ConfirmDialog from "@/components/ConfirmDialog";
const TradeChart = lazy(() => import("@/components/TradeChart"));
interface PositionCardProps {
  position: Position;
  onActionComplete?: () => void;
}
function formatPrice(value: number): string {
  if (value >= 1000) return value.toFixed(0);
  if (value >= 1) return value.toFixed(2);
  return value.toFixed(6);
}
function formatDuration(openedAt: number, closedAt: number | null): string {
  const end = closedAt ?? Date.now();
  const minutes = Math.max(0, Math.floor((end - openedAt) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}
export default function PositionCard({ position, onActionComplete }: PositionCardProps) {
  const [confirmAction, setConfirmAction] = useState<"close" | "cancel-orders" | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [chartOpen, setChartOpen] = useState(position.status === "open");
  const directionColor = position.direction === "long" ? "text-gold" : "text-sell";
  const isOpen = position.status === "open";
  const pnl = position.realizedPnlUsd ?? position.unrealizedPnlUsd ?? null;
  const currentPrice = position.currentPrice ?? null;
  const runAction = async (action: "close" | "cancel-orders") => {
    setBusy(true);
    setActionError(null);
    try {
      const result = action === "close" ? await closePosition(position.id) : await cancelPendingOrders(position.id);
      if (!result.ok) {
        setActionError(result.errorMessage ?? "Action failed");
      } else {
        onActionComplete?.();
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Action failed");
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };
  return (
    <div className="rounded-xl border border-line bg-panel p-4 shadow-panel">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-ink">{position.symbol}</span>
            <span className={`font-mono text-xs font-semibold uppercase ${directionColor}`}>{position.direction}</span>
            <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              {position.market}
            </span>
          </div>
          <div className="mt-1 font-mono text-[11px] text-ink-faint">
            {position.leverage}x · {position.timeframe} · {formatDuration(position.openedAt, position.closedAt)}
          </div>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide ${position.dryRun ? "bg-paper/10 text-paper" : "bg-live/10 text-live"}`}
        >
          {position.dryRun ? "Paper" : "Live"}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[11px]">
        <div className="min-w-0">
          <div className="text-ink-faint">Entry</div>
          <div className="tabular break-all text-ink">{formatPrice(position.entryPrice)}</div>
        </div>
        <div className="min-w-0">
          <div className="text-ink-faint">Stop Loss</div>
          <div className="tabular break-all text-sell">{formatPrice(position.slPrice)}</div>
        </div>
        <div className="min-w-0">
          <div className="text-ink-faint">Size</div>
          <div className="tabular break-all text-ink">${position.positionSizeUsd.toFixed(2)}</div>
        </div>
      </div>

      {currentPrice !== null && (
        <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px]">
          <div className="min-w-0">
            <div className="text-ink-faint">Current</div>
            <div className="tabular break-all text-ink">{formatPrice(currentPrice)}</div>
          </div>
          <div className="min-w-0">
            <div className="text-ink-faint">Unrealized PnL</div>
            <div className={`tabular break-all font-semibold ${pnl !== null && pnl >= 0 ? "text-profit" : "text-sell"}`}>
              {pnl !== null ? `${pnl >= 0 ? "+" : ""}$${pnl!.toFixed(2)}` : "—"}
            </div>
          </div>
        </div>
      )}

      {position.tpLevels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 font-mono text-[10px] text-ink-dim">
          <span className="text-ink-faint">TP</span>
          {position.tpLevels.map((level, index) => (
            <span key={index} className="rounded border border-line-soft px-1.5 py-0.5 text-gold">
              {formatPrice(level)}
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setChartOpen((value) => !value)}
        aria-expanded={chartOpen}
        className="mt-3 flex w-full items-center justify-between rounded-md border border-line-soft px-2 py-1.5 font-mono text-[11px] text-ink-dim transition hover:border-turquoise/50 hover:text-turquoise"
      >
        <span>{chartOpen ? "Hide chart" : "Show chart"}</span>
        <span aria-hidden>{chartOpen ? "▴" : "▾"}</span>
      </button>
      {chartOpen && (
        <Suspense
          fallback={
            <div className="mt-3 border-t border-line-soft pt-3 text-center font-mono text-[11px] text-ink-faint">
              Loading chart…
            </div>
          }
        >
          <TradeChart position={position} />
        </Suspense>
      )}

      {!isOpen && (
        <div className="mt-3 flex items-center justify-between border-t border-line-soft pt-3 font-mono text-xs">
          <span className="text-ink-faint">{position.closeReason ?? "closed"}</span>
          <span className={`font-semibold tabular ${(pnl ?? 0) >= 0 ? "text-profit" : "text-sell"}`}>
            {pnl !== null ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(2)}` : "—"}
          </span>
        </div>
      )}

      {isOpen && (
        <div className="mt-3 flex flex-col gap-1.5 border-t border-line-soft pt-3">
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmAction("close")}
              className="flex-1 rounded-md border border-sell-dim/50 px-2 py-1.5 font-mono text-[11px] font-semibold text-sell transition hover:bg-sell/10"
            >
              Close Position
            </button>
            {position.market === "futures" && (
              <button
                onClick={() => setConfirmAction("cancel-orders")}
                className="flex-1 rounded-md border border-line px-2 py-1.5 font-mono text-[11px] text-ink-dim transition hover:border-gold/50 hover:text-gold"
              >
                Cancel Orders
              </button>
            )}
          </div>
          {actionError && <div className="font-mono text-[10px] text-sell">{actionError}</div>}
        </div>
      )}

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction === "close" ? `Close ${position.symbol}?` : `Cancel pending orders for ${position.symbol}?`}
        message={
          confirmAction === "close"
            ? `This immediately closes the ${position.direction} position at market price. This cannot be undone.`
            : "This cancels the exchange-side stop-loss/take-profit orders. The position stays open and remains software-monitored, but loses exchange-native protection until new orders are placed."
        }
        confirmLabel={confirmAction === "close" ? "Close Position" : "Cancel Orders"}
        busy={busy}
        onConfirm={() => confirmAction && runAction(confirmAction)}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
